import Vue from 'vue'
import Vuex from 'vuex'
import authModule from './modules/authentication'
import userModule from './modules/user'
import companyModule from './modules/company'
import uiSettingsModule from './modules/ui-settings'
import authApiClient from '../api/auth-api-client'
import adminApiClient from '../api/admin-api-client'
import { groupValue } from '../util/enum'
import { loadLanguageAsync } from '../i18n'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isLoading: false,
  },
  mutations: {
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading
    },
  },
  actions: {
    async init(context) {
      try {
        context.commit('setIsLoading', true)

        let userInfo = await authApiClient.getUserTokenInfo()

        const isExternalUser =
          userInfo.data?.group?.groupName === 'PortalExternalUser' || userInfo.data?.group?.groupId === 9

        if (isExternalUser) {
          const availableCompanies = await authApiClient.get('Authentication/availablecompanies?PageSize=25')

          const selectedCompany = availableCompanies.data.data[0]
          if (selectedCompany) {
            const contextResponse = await authApiClient.post('Authentication/changecontext', {
              authContext: {
                companyId: selectedCompany.companyId,
              },
              deviceId: localStorage.getItem('deviceId') || crypto.randomUUID(),
            })

            if (contextResponse.data.token) {
              await context.dispatch('auth/setToken', { token: contextResponse.data.token })
            }

            userInfo = await authApiClient.getUserTokenInfo()
          }
        }

        if (userInfo.data && userInfo.data.group.groupValue >= groupValue.PortalUserAdmin) {
          context.dispatch('user/setUserTokenInfo', userInfo.data)

          // Set authenticated user context for Application Insights

          // Use the proper method to set authenticated user context

          let fallBack = navigator.language ? navigator.language.split('-')[0] : 'en'
          let langProp = userInfo.data.properties.find((prop) => prop.propertyId === 5)
          let lang = langProp ? langProp.propertyValue.toLowerCase() : fallBack
          await loadLanguageAsync(lang)

          let userApps = await adminApiClient.get('Applications')
          let userProps = await adminApiClient.get('Properties')
          let userGroups = await adminApiClient.get('Groups')
          let passwordPolicies = await adminApiClient.get('PasswordPolicies')
          let locales = await adminApiClient.getTranslationsLanguages()
          context.dispatch('user/setUserProperties', userProps.data.results)
          context.dispatch('user/setUserApplications', userApps.data.results)
          context.dispatch('user/setUserGroups', userGroups.data.results)
          context.dispatch('user/setPasswordPolicies', passwordPolicies.data.results)
          context.dispatch('user/setLocales', locales.data)

          if (userInfo.data.group.groupValue >= groupValue.PartnerAdmin) {
            let companyCategories = await adminApiClient.get('CompanyCategories')
            context.dispatch('user/setCompanyCategories', companyCategories.data.results)
            context.dispatch('user/setImpersonateAccount', JSON.parse(localStorage.getItem('impersonateAccount')))
            const currentUser = userInfo.data.userId
            const userResult = await adminApiClient.getUsers({
              Page: 0,
              PageSize: 42,
              Filter: 'userId=' + currentUser + '/i',
              orderBy: 'userId asc',
            })
            if (userResult.data.count === 1) {
              const userCompany = {
                companyId: userResult.data.data[0].companyId,
                companyName: userResult.data.data[0].companyName,
              }
              context.dispatch('user/setUserOriginalCompany', userCompany)
            }
          }

          context.commit('auth/setIsLoggedIn', true)
          const token = context.rootState.auth.token
          const user = JSON.parse(atob(token.split('.')[1]))
          context.commit('user/setUserId', user.UserDbId)

          // Set enhanced user information for Site24x7 RUM tracking
          if (Vue.prototype.$rum && Vue.prototype.$rum.isAvailable()) {
            const enhancedUserInfo = {
              id: userInfo.data.userDbId,
              userId: userInfo.data.userId,
              name: userInfo.data.userName,
              email: userInfo.data.userId, // userId is typically the email
              userRole: userInfo.data.group?.groupName || 'user',
              companyId: userInfo.data.companyId,
              companyName: userInfo.data.company?.companyName,
            }

            Vue.prototype.$rum.setUserId(enhancedUserInfo)

            // Also track the user session
            Vue.prototype.$rum.trackUserSession({
              sessionId: 'session_' + Date.now(),
              userId: userInfo.data.userDbId,
              userName: userInfo.data.userName,
              email: userInfo.data.userId,
              userRole: userInfo.data.group?.groupName || 'user',
              companyId: userInfo.data.companyId,
              companyName: userInfo.data.company?.companyName,
              loginTime: new Date().toISOString(),
            })
          }

          return true
        } else {
          const error = new Error('Insufficient permissions')
          error.code = 'ACCESS_DENIED'
          throw error
        }
      } catch (ex) {
        if (ex.code === 'ACCESS_DENIED') {
          throw ex
        }
        context.dispatch('auth/logout')
      } finally {
        context.commit('setIsLoading', false)
      }
    },
  },

  modules: {
    auth: authModule,
    user: userModule,
    ui: uiSettingsModule,
    company: companyModule,
  },
})
